/**
 * The page's one connection to OpenCode on the person's own computer. The
 * desktop app's plugin installs and starts the server and hands the page its
 * address and password through `start()`; in development the pair is stored
 * in `localStorage` first. The page keeps the pair in memory only, runs
 * the client of `src/lib/code-sessions/local-agent/operations.ts` on it and
 * keeps the event stream to it, so the desktop app and a plain browser take
 * one path. `start()` runs once per page load before the first call, and
 * again before each Retry, which asks the plugin to replace a running server
 * that no longer answers; callers reach it through `ensure()` and `retry()`
 * alone. Only the first use of This computer may install the agent: any
 * other first start asks `status()` first. That client, with OpenCode's own
 * inside it, is imported only once the connection is made, which needs the
 * plugin or a development pair: a plain browser's `/code` never downloads it.
 * The stream connects again when it goes quiet (`runEventStream`) and when a
 * start hands over another server; a page that shows again, or comes back
 * online, keeps it, since a reconnect drops the text a turn has streamed so
 * far. A call or the stream that finds nothing answering on the address, as
 * after a crash, starts the server again by itself a moment later, unless the
 * page is going, once in 30 s at most, and no more after such a start failed
 * until the person's Retry or a call the server answers.
 */
import type { OperationResult } from '@/lib/code-sessions/local-agent/errors';
import type {
  FetchLike,
  OpenCodeServerAddress,
} from '@/lib/code-sessions/local-agent/opencode-api';
import type { LocalAgentClient } from '@/lib/code-sessions/local-agent/operations';
import type {
  PluginOperationName,
  PluginOperations,
  StartInput,
  StatusReport,
} from '@/lib/code-sessions/local-agent/plugin-api';

import type { SessionConnection } from '../types';

import { sessionElsewhere, toLocalRequestError } from './local-errors';
import { feedLocalSessions } from './local-events';
import { refreshLocalList } from './local-list';
import { storedGrants } from './stored-grants';
import { storedTail } from './stored-tail';

import { clientLogger } from '@/lib/client-logger';
import { unwrapOperationResult } from '@/lib/code-sessions/local-agent/errors';
import { changesAsks, runEventStream } from '@/lib/code-sessions/local-agent/event-tail';
import { Env } from '@/lib/env';

/**
 * What the page calls: the client of `operations.ts`, the plugin's `status()`
 * and `pickFolder()`, and `ensure()` and `retry()` in place of the plugin's
 * `start()`, so the only address in play is the one the connection holds; one
 * a caller took from a `start()` of its own would go stale under it. Nothing
 * on the page stops the server, and the connection would start a stopped one
 * again at its next failed call, so the plugin's `stop()` is not offered.
 */
export type LocalAgentConnection = LocalAgentClient &
  Omit<PluginOperations, 'start' | 'stop'> & {
    /**
     * The server's address and password: `start()` once per page load, shared by every caller.
     * Only the first use of This computer installs the agent (`install`). Any other first start of
     * a page load asks `status()` first, and where nothing is installed or running fails with no
     * Retry: the session runs in the cubic desktop app on the computer that started it.
     */
    ensure: (options?: { install?: boolean }) => Promise<OpenCodeServerAddress>;
    /**
     * `start()` again, before a Retry; the pair it returns replaces the one the page holds. A
     * Retry while one runs joins it.
     */
    retry: () => Promise<OpenCodeServerAddress>;
    /** How the page's event stream stands; `live` until the first start opens it. */
    streamState: () => SessionConnection;
    /** Calls back when `streamState()` changes; returns the unsubscribe. */
    watchStream: (onChange: () => void) => () => void;
  };

/**
 * Where development stores the server's address and password, and the folder
 * `pickFolder()` answers with, so the composer runs in a plain browser:
 * `{ url, password, folder? }`.
 */
export const DEVELOPMENT_CONNECTION_STORAGE_KEY = 'cubic:local-agent';

type DevelopmentSettings = { url: string; password: string; folder?: string };

const DEVELOPMENT_STATUS: StatusReport = {
  platform: 'development',
  pluginVersion: 'development',
  openCodeVersion: null,
  running: true,
};

/** What an operation resolves with, which the preload's bridge wraps in an `OperationResult`. */
type PluginValue<Name extends PluginOperationName> =
  ReturnType<PluginOperations[Name]> extends Promise<infer Value> ? Value : never;

/**
 * The preload's bridge: each operation answers with the `OperationResult` of its value. `start`
 * passes the page's input on, and the preload sends the main process its `check` flag alone
 * (`preload.ts`), so an absent input reaches it as none.
 */
type PluginBridge = {
  [Name in PluginOperationName]: (
    ...args: Parameters<PluginOperations[Name]>
  ) => Promise<OperationResult<PluginValue<Name>>>;
};

/**
 * The plugin's four operations from `window.todesktop.cubicLocal`, when the
 * desktop app's preload put them there. Each resolves with an
 * `OperationResult`, since Electron flattens a thrown error on the way
 * through IPC; the page throws the `LocalAgentError` it stands for.
 */
function pluginOperations(): PluginOperations | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const bridge = (window as { todesktop?: { cubicLocal?: PluginBridge | null } }).todesktop
    ?.cubicLocal;
  if (bridge === undefined || bridge === null) {
    return null;
  }
  return {
    start: async (input) => unwrapOperationResult(await bridge.start(input)),
    stop: async () => unwrapOperationResult(await bridge.stop()),
    status: async () => unwrapOperationResult(await bridge.status()),
    pickFolder: async () => unwrapOperationResult(await bridge.pickFolder()),
  };
}

function developmentSettings(): DevelopmentSettings | null {
  if (Env.NODE_ENV !== 'development' || typeof window === 'undefined') {
    return null;
  }
  const raw = window.localStorage.getItem(DEVELOPMENT_CONNECTION_STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<DevelopmentSettings>;
    if (typeof parsed.url !== 'string' || typeof parsed.password !== 'string') {
      return null;
    }
    return {
      url: parsed.url.replace(/\/$/, ''),
      password: parsed.password,
      ...(typeof parsed.folder === 'string' ? { folder: parsed.folder } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * The plugin's operations as development stands in for them: the stored pair,
 * and the folder it names. `start()` reads the key again each time, so a restarted
 * server is played by storing its new pair before Retry.
 */
function developmentOperations(initial: DevelopmentSettings): PluginOperations {
  const current = () => developmentSettings() ?? initial;
  return {
    start: async () => {
      const settings = current();
      return { ...DEVELOPMENT_STATUS, url: settings.url, password: settings.password };
    },
    stop: async () => ({ ...DEVELOPMENT_STATUS, running: false }),
    status: async () => DEVELOPMENT_STATUS,
    pickFolder: async () => {
      const { folder } = current();
      return folder === undefined
        ? null
        : {
            path: folder,
            name: folder.split('/').filter(Boolean).at(-1) ?? folder,
          };
    },
  };
}

/**
 * How often the page starts the server again by itself after nothing answered
 * on its address: once in 30 s at most, so a server that keeps stopping waits
 * for the person's Retry, which runs whenever it is pressed. A start of its
 * own that failed holds back the next until that Retry, or a call the server
 * answers: a broken install would otherwise spawn, read a login shell or
 * download in the background every 30 s.
 */
const AUTOMATIC_RESTART_INTERVAL_MS = 30_000;

/**
 * How long the page waits after a request found nothing answering before it
 * starts the server again by itself. A reload or a navigation cancels the
 * page's requests, which then fail as a crash's do, a few milliseconds before
 * `pagehide` says the page is going (Chrome: about 3 ms); by the end of the
 * wait, a page that is going is gone, or has said so.
 */
export const AUTOMATIC_RESTART_DELAY_MS = 300;

/** The page's client, once imported, and the module it came from. */
type LoadedClient = {
  module: typeof import('@/lib/code-sessions/local-agent/operations');
  client: LocalAgentClient;
};

/**
 * The connection over the plugin's operations: `start()` once, the client on
 * the pair it returned, and the event stream to the same pair. A client call
 * waits for `start()` first, so the first local read of a page load starts
 * the server where the agent is installed or running, and the stream with
 * it; a `start()` that failed is not cached, and `retry()` runs it again. A
 * start that hands over another pair drops the stream's connection, so it
 * reconnects to the new pair at once. The client is imported as the
 * connection is made, before any start: a desktop window runs one bundle for
 * days, and a chunk it asked for only days later could be gone after a
 * deploy. The stream feeds every opened session, view or no view
 * (`feedLocalSessions`). A session's ask, or its answer, tells the sidebar's
 * list to read again. A call that finds nothing answering on the address
 * starts the server again (`restartAfterCrash`), unless the page is
 * unloading. `close` ends the stream and stops listening to the tail and to
 * the page.
 */
function connect(operations: PluginOperations): {
  connection: LocalAgentConnection;
  close: () => void;
} {
  let pair: OpenCodeServerAddress | null = null;
  let started: Promise<OpenCodeServerAddress> | null = null;
  // The Retry's start that runs now, which another Retry joins, and whether the person's Retry
  // asked for it, by itself or by joining the page's own: then what follows is theirs to choose.
  let retrying: { running: Promise<OpenCodeServerAddress>; asked: boolean } | null = null;
  // When the server was last started again by the page itself.
  let restartedAt: number | null = null;
  // Whether that start failed, with no Retry pressed and no call answered since.
  let restartFailed = false;
  // The page's own start that a failure scheduled, which every failure until it ends joins.
  let scheduled: Promise<boolean> | null = null;
  // The latest start a Retry made, the person's or the page's own.
  let lastRestart: Promise<OpenCodeServerAddress> | null = null;
  // Whether the page is going: a reload or a navigation cancels its requests, which then fail as
  // if nothing answered on the address. That is no crash, so nothing starts the server for it:
  // the start a crash makes checks that the server answers, and would replace one that is only
  // busy, cutting its turn short. A page the back-forward cache shows again is back.
  let unloading = false;
  let streaming = false;
  let streamConnected = false;
  const streamWatchers = new Set<() => void>();
  const streamState = (): SessionConnection =>
    !streaming || streamConnected ? 'live' : restartFailed ? 'offline' : 'reconnecting';
  let reportedStream = streamState();
  const reportStream = () => {
    const state = streamState();
    if (state !== reportedStream) {
      reportedStream = state;
      streamWatchers.forEach((onChange) => onChange());
    }
  };
  const setRestartFailed = (failed: boolean) => {
    restartFailed = failed;
    reportStream();
  };
  const closing = new AbortController();
  // Drops the stream's connection, once the stream runs.
  let dropStream: (() => void) | null = null;
  if (typeof window !== 'undefined') {
    window.addEventListener(
      'pagehide',
      () => {
        unloading = true;
        // Ended on purpose, the stream's request closes quietly instead of failing as the page
        // tears it down.
        dropStream?.();
      },
      { signal: closing.signal },
    );
    window.addEventListener(
      'pageshow',
      () => {
        unloading = false;
      },
      { signal: closing.signal },
    );
  }
  const fetchLike: FetchLike = (input, init) => fetch(input, init);
  const storage = typeof sessionStorage === 'undefined' ? null : sessionStorage;
  const tail = storedTail(storage);
  // Every opened session takes the stream from here, view or no view, so a view opened again
  // finds its log whole.
  const stopFeeding = tail.subscribe(feedLocalSessions);
  // A session that asks the person something, or gets its answer, is marked or cleared in the
  // sidebar's list, which reads the asks of every session that runs; so is any after a reconnect
  // missed events.
  const stopWatchingAsks = tail.subscribe((signal) => {
    if (changesAsks(signal)) {
      refreshLocalList();
    }
  });
  // The consent guard's lines go to the console, so a request that reads as Deny alone leaves
  // why ("could not read the tool call behind a permission request") where a report can find it.
  const log = (message: string, detail?: Record<string, unknown>) =>
    clientLogger.info(`OpenCode on this computer: ${message}`, detail);
  let loaded: Promise<LoadedClient> | null = null;
  // An import that failed is not cached: the next call imports again.
  const load = () =>
    (loaded ??= import('@/lib/code-sessions/local-agent/operations').then(
      (module) => ({
        module,
        client: module.createLocalAgentClient({
          server: () => pair,
          fetch: fetchLike,
          tail,
          log,
          // "Allow for this session" lasts through a reload of this tab, as the tail does.
          grants: storedGrants(storage),
        }),
      }),
      (error: unknown) => {
        loaded = null;
        throw error;
      },
    ));
  void load().catch(() => undefined);
  const startServer = (input?: StartInput) =>
    (started ??= Promise.all([operations.start(input), load()]).then(
      ([result, { module }]) => {
        const previous = pair;
        pair = { url: result.url, password: result.password };
        // A Retry that replaced the server hands over another pair: the stream's connection to the
        // old one may hang rather than fail, so it is dropped and made again to the new one.
        if (
          previous !== null &&
          (previous.url !== pair.url || previous.password !== pair.password)
        ) {
          dropStream?.();
        }
        // The stream starts with the first server, once per page load, and follows the pair the
        // connection holds from then on. A page that never starts a server runs no stream, so a
        // desktop /code page that never picks This computer wakes nothing.
        if (!streaming) {
          streaming = true;
          reportStream();
          void runEventStream({
            subscribe: (server, options) =>
              module.subscribeEvents({ fetch: fetchLike, server, log }, options),
            server: () => pair,
            tail,
            signal: closing.signal,
            sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
            // A failed connection reconnects on its own; the console keeps why it failed. One that
            // finds nothing answering on the address starts the server again, as a call does.
            onError: (error) => {
              clientLogger.warn('The event stream from OpenCode on this computer failed', error);
              if (error instanceof TypeError) {
                void restartAfterCrash();
              }
            },
            onConnectionChange: (connected) => {
              streamConnected = connected;
              reportStream();
            },
            watch: (drop) => {
              dropStream = drop;
            },
          });
        }
        // A server that started ends the failure an earlier read of the sidebar's list may have
        // met, so the list reads again, from the first start and from every retry.
        refreshLocalList();
        return pair;
      },
      (error: unknown) => {
        started = null;
        throw error;
      },
    ));
  const ensure = async ({ install = false }: { install?: boolean } = {}) => {
    // `start()` installs the agent where nothing is installed, which only the first use of This
    // computer may do. Any other first start, as a link to a session opened in a window that never
    // used This computer, asks first.
    if (started === null && !install) {
      const { running, openCodeVersion } = await operations.status();
      if (!running && openCodeVersion === null) {
        throw sessionElsewhere();
      }
    }
    return startServer();
  };
  /** The start a Retry makes, one at a time: another asked for while it runs joins it. */
  const restart = () => {
    if (retrying !== null) {
      return retrying;
    }
    started = null;
    // The person pressed Retry after a call failed, or a call found nothing answering
    // (`restartAfterCrash`): the plugin also checks that a running server still answers and
    // replaces one that does not. A page load's start never checks that, so a server that stalls a
    // moment while another window opens keeps its turn. Any start may still read which sessions
    // run, when the server runs another OpenCode version than the launch pins, to replace it once
    // none does.
    const run = {
      running: startServer({ check: true }).finally(() => {
        retrying = null;
      }),
      asked: false,
    };
    retrying = run;
    lastRestart = run.running;
    return run;
  };
  // The person's Retry, whatever it ends in, lets the page start the server by itself again.
  const retry = () => {
    setRestartFailed(false);
    const run = restart();
    run.asked = true;
    return run.running;
  };
  /**
   * Starts the server again after nothing answered on its address, as a crash leaves it, with the
   * start a Retry makes, so the plugin also replaces a running server that no longer answers. It
   * waits `AUTOMATIC_RESTART_DELAY_MS` first, which every failure until it is done joins, and
   * starts nothing for a page that is unloading: its own requests fail as it goes, a moment before
   * it says so. A start already running is joined. The page begins one by itself once in
   * `AUTOMATIC_RESTART_INTERVAL_MS` at most, and none after one of its own failed until the
   * person's Retry or a call the server answers. Resolves whether a server was started.
   */
  function restartAfterCrash(): Promise<boolean> {
    if (scheduled === null) {
      const before = lastRestart;
      scheduled = new Promise((resolve) => setTimeout(resolve, AUTOMATIC_RESTART_DELAY_MS))
        .then(() => restartNow(before))
        .finally(() => {
          scheduled = null;
        });
    }
    return scheduled;
  }
  /**
   * `restartAfterCrash` once its wait is over. `before` is the latest start a Retry made when the
   * failure came: one made since, by the person's Retry above all, answers for the failure, so the
   * page starts no second server after it, which would replace the one that Retry handed over.
   */
  async function restartNow(before: Promise<OpenCodeServerAddress> | null): Promise<boolean> {
    if (unloading) {
      return false;
    }
    if (lastRestart !== null && lastRestart !== before) {
      return lastRestart.then(
        () => true,
        () => false,
      );
    }
    if (retrying !== null) {
      return retrying.running.then(
        () => true,
        () => false,
      );
    }
    const now = Date.now();
    if (
      restartFailed ||
      (restartedAt !== null && now - restartedAt < AUTOMATIC_RESTART_INTERVAL_MS)
    ) {
      return false;
    }
    restartedAt = now;
    const run = restart();
    try {
      await run.running;
      return true;
    } catch (error) {
      // A Retry the person pressed while it ran made it theirs: whatever it ended in, the next stop
      // may be mended by itself, and a call starts the server again as after any failed Retry. The
      // flag stays on the start, since its `finally` has cleared `retrying` by now.
      if (!run.asked) {
        setRestartFailed(true);
        // Calls go on to the server that stopped and show that nothing answers there, with Retry,
        // as before the page started servers by itself, rather than each starting one.
        if (started === null && pair !== null) {
          started = Promise.resolve(pair);
        }
      }
      clientLogger.warn('OpenCode on this computer did not start again after it stopped', error);
      return false;
    }
  }
  /**
   * A call of the page's client, made once `start()` handed over a server and the client loaded.
   * One that finds nothing answering on the address starts the server again first
   * (`restartAfterCrash`). A read or a wait, a prompt or a compaction, which OpenCode keeps under
   * the id it carries, a rename, which only sets the same title again, an approval mode, which
   * sets the same rules again, a revert's stage, which ends the same however often it is made, or
   * an archive or an unarchive, which each leave the session as asked when made twice, is then
   * made again; any other call throws its failure for the person's Retry, since making it again
   * could do it twice.
   */
  const ensured =
    <Args extends unknown[], Result>(
      pick: (client: LocalAgentClient) => (...args: Args) => Promise<Result>,
      afterRestart: 'repeat' | 'throw' = 'throw',
    ) =>
    async (...args: Args): Promise<Result> => {
      await ensure();
      const { client } = await load();
      const call = async () => {
        const result = await pick(client)(...args);
        // The server answers: the page may start it by itself again after its next stop.
        setRestartFailed(false);
        return result;
      };
      try {
        return await call();
      } catch (error) {
        // The browser's own failure to reach the server (`local-errors.ts`). A failure status, a
        // deadline or an abort is the server's answer or the caller's, which no start mends.
        if (
          !(error instanceof TypeError) ||
          !(await restartAfterCrash()) ||
          afterRestart === 'throw'
        ) {
          throw error;
        }
        return call();
      }
    };
  const connection: LocalAgentConnection = {
    status: operations.status,
    pickFolder: operations.pickFolder,
    ensure,
    retry,
    listSessions: ensured((client) => client.listSessions, 'repeat'),
    createSession: ensured((client) => client.createSession),
    deleteSession: ensured((client) => client.deleteSession),
    renameSession: ensured((client) => client.renameSession, 'repeat'),
    archiveSession: ensured((client) => client.archiveSession, 'repeat'),
    unarchiveSession: ensured((client) => client.unarchiveSession, 'repeat'),
    getSnapshot: ensured((client) => client.getSnapshot, 'repeat'),
    inbox: ensured((client) => client.inbox, 'repeat'),
    active: ensured((client) => client.active, 'repeat'),
    contextWindow: ensured((client) => client.contextWindow, 'repeat'),
    prompt: ensured((client) => client.prompt, 'repeat'),
    compact: ensured((client) => client.compact, 'repeat'),
    interrupt: ensured((client) => client.interrupt),
    setDelivery: ensured((client) => client.setDelivery),
    cancelQueued: ensured((client) => client.cancelQueued),
    answerForm: ensured((client) => client.answerForm),
    cancelForm: ensured((client) => client.cancelForm),
    answerPermission: ensured((client) => client.answerPermission),
    findFiles: ensured((client) => client.findFiles, 'repeat'),
    listSkills: ensured((client) => client.listSkills, 'repeat'),
    setApprovalMode: ensured((client) => client.setApprovalMode, 'repeat'),
    waitIdle: ensured((client) => client.waitIdle, 'repeat'),
    stageRevert: ensured((client) => client.stageRevert, 'repeat'),
    // A clear wakes the session, so made again it could meet the work it woke.
    clearRevert: ensured((client) => client.clearRevert),
    fork: ensured((client) => client.fork),
    streamState,
    watchStream: (onChange) => {
      streamWatchers.add(onChange);
      return () => {
        streamWatchers.delete(onChange);
      };
    },
  };
  return {
    connection,
    close: () => {
      closing.abort();
      stopFeeding();
      stopWatchingAsks();
    },
  };
}

let connected: ReturnType<typeof connect> | null = null;

/**
 * The connection this page has to the agent on this computer: the plugin's
 * in the desktop app, else the development pair when one is stored, else
 * none. Made once per page load and kept: the plugin is there from the first
 * script, and a development pair is stored before the page loads.
 */
export function localAgentConnection(): LocalAgentConnection | null {
  if (connected !== null) {
    return connected.connection;
  }
  const plugin = pluginOperations();
  if (plugin !== null) {
    connected = connect(plugin);
    return connected.connection;
  }
  const settings = developmentSettings();
  if (settings === null) {
    return null;
  }
  connected = connect(developmentOperations(settings));
  return connected.connection;
}

/**
 * `start()` again before a Retry: a failed load or send may follow a server
 * that stopped, and the plugin starts it again and hands the page the new
 * pair. The caller's own retry runs next. A start that fails rejects with
 * the failure in plain words (`local-errors.ts`), so the caller shows why
 * and skips its retry, instead of a read that would start the server a
 * second time only to meet the same failure.
 */
export async function retryLocalAgent(): Promise<void> {
  try {
    await localAgentConnection()?.retry();
  } catch (error) {
    throw toLocalRequestError(error) ?? error;
  }
}

/**
 * Test support: ends the connection's stream and its listeners on the tail
 * and the page, and forgets it, so the next read builds one from the current
 * plugin or settings.
 */
export function resetLocalAgentConnection() {
  connected?.close();
  connected = null;
}
